import { createSlice } from '@reduxjs/toolkit'
import Auth from '@aws-amplify/auth'
import aws4 from 'aws4'
import _ from 'lodash'
import {
  API_ENDPOINT,
  API_PROTOCOL,
  API_REGION,
  API_STAGE,
  SCOUT_API_ENDPOINT,
  SCOUT_API_PROTOCOL,
  SCOUT_API_REGION,
  SCOUT_API_STAGE,
  ERP_API_ENDPOINT,
  ERP_API_PROTOCOL,
  ERP_API_REGION,
  ERP_API_STAGE
} from './auth'

const API_DEBUG_LOGS = String(process.env.REACT_APP_API_DEBUG_LOGS || '').toLowerCase() === 'true'
const apiDebug = (...args) => {
  if (API_DEBUG_LOGS) console.log(...args)
}

function getReadingTime(reading) {
  if (!reading) return null
  const timestamp = Date.parse(reading)
  return Number.isNaN(timestamp) ? null : timestamp
}

function keepNewestReading(incoming, current) {
  const incomingTime = getReadingTime(incoming?.lastReading)
  const currentTime = getReadingTime(current?.lastReading)

  if (incomingTime !== null && currentTime !== null && incomingTime < currentTime) {
    return current
  }

  return incoming
}

function mergeFreshAssetReadings(incomingAsset, currentAsset) {
  if (!incomingAsset || !currentAsset) return incomingAsset

  const currentInputsBySk = new Map((currentAsset.inputs || []).map(input => [input.SK, input]))
  const inputs = (incomingAsset.inputs || []).map(input =>
    keepNewestReading(input, currentInputsBySk.get(input.SK))
  )
  const currentOutputsBySk = new Map((currentAsset.outputs || []).map(output => [output.SK, output]))
  const outputs = (incomingAsset.outputs || []).map(output =>
    keepNewestReading(output, currentOutputsBySk.get(output.SK))
  )
  const input = keepNewestReading(
    incomingAsset.input,
    currentAsset.input?.SK === incomingAsset.input?.SK ? currentAsset.input : currentInputsBySk.get(incomingAsset.input?.SK)
  )
  const output = keepNewestReading(
    incomingAsset.output,
    currentAsset.output?.SK === incomingAsset.output?.SK ? currentAsset.output : currentOutputsBySk.get(incomingAsset.output?.SK)
  )

  return {
    ...incomingAsset,
    input,
    inputs,
    output,
    outputs
  }
}

const initialState = {
  config: null,
  site: null,
  sites: null,
  assets: [],
  energy: null,
  power: null,
  siteLogs: [],
  templates: [],
  user: null,
  apiKeys: [],
  plaatoDevices: [],
  amberPrices: [],
  triageBoard: null,
  assistantEfficiencyForecast: null,
  scoutRefresh: null,
  scoutActionHistory: null,
  scoutContext: null,
  scoutChat: null,
  // ERP
  erpProductTypes: [],
  erpProductType: null,
  erpProcessFlows: [],
  erpProcessFlow: null,
  erpIngredients: [],
  erpIngredient: null,
  erpSuppliers: [],
  erpSupplier: null,
  erpRecipes: [],
  erpRecipe: null,
  erpInventoryItems: [],
  erpInventoryTransactions: [],
  erpInventoryLots: [],
  erpBatches: [],
  erpBatch: null,
  erpBatchLogs: [],
  erpBatchLabs: [],
  erpAssets: [],
  erpPackagingItems: [],
  erpPackagingItem: null,
  erpPackagingRuns: [],
  erpPackagingRun: null,
  erpOtherItems: [],
  erpOtherItem: null,
  erpSkus: [],
  erpSku: null,
  erpExciseConfig: null,
  erpExciseRates: [],
  erpAmr: null,
  erpExciseReturns: [],
  erpExciseReturn: null,
  erpDispatches: [],
  erpExciseRecords: [],
  erpRecallTrace: null,
  erpCustomers: [],
  erpCustomer: null,
  erpOrders: [],
  erpOrder: null,
  erpBarrels: [],
  erpBarrel: null,
  erpBarrelEvents: [],
  erpPickingRuns: [],
  erpPickingRun: null,
  erpShipments: [],
  erpShipment: null,
  erpPurchaseOrders: [],
  erpPurchaseOrder: null,
  erpResources: [],
  erpResource: null,
  erpResourceEvents: [],
  erpResourceEvent: null,
  erpTrashEvents: [],
  erpXeroStatus: null,
  erpXeroAuthUrl: null,
  erpXeroConfig: null,
  erpSquareStatus: null,
  erpSquareAuthUrl: null,
  erpSquareConfig: null,
  erpGotabStatus: null,
  erpGotabConfig: null,
  erpKountaStatus: null,
  erpKountaAuthUrl: null,
  erpKountaConfig: null
}

const SCOUT_API_PATHS = new Set([
  '/assistant/triageboard/get',
  '/assistant/asset/insight',
  '/assistant/inferencechart/get',
  '/assistant/scout/refresh',
  '/assistant/scout/refresh/quota',
  '/assistant/scout/tank/refresh',
  '/assistant/actions/execute',
  '/assistant/actions/history',
  '/assistant/scout/context',
  '/assistant/chat',
  '/assistant/models/bootstrap',
  '/assistant/models/training/set',
  '/assistant/models/batch/clear',
  '/assistant/models/retrain',
  '/assistant/efficiency/train',
  '/assistant/efficiency/forecast',
  '/assistant/efficiency/forecast/get'
])

// All ERP requests route to the separate ErpApi gateway
const ERP_API_PREFIX = '/erp/'

function isErpPath(path) {
  return typeof path === 'string' && path.startsWith(ERP_API_PREFIX)
}

function normalizeProtocol(protocol) {
  return String(protocol || 'https').replace(/:$/, '')
}

function resolveApiTarget(path) {
  const defaultTarget = {
    protocol: normalizeProtocol(API_PROTOCOL),
    host: API_ENDPOINT,
    basePath: API_STAGE,
    region: API_REGION
  }

  if (isErpPath(path) && ERP_API_ENDPOINT) {
    return {
      protocol: normalizeProtocol(ERP_API_PROTOCOL),
      host: ERP_API_ENDPOINT,
      basePath: ERP_API_STAGE || defaultTarget.basePath,
      region: ERP_API_REGION || defaultTarget.region
    }
  }

  if (!SCOUT_API_PATHS.has(path) || !SCOUT_API_ENDPOINT) {
    return defaultTarget
  }

  return {
    protocol: normalizeProtocol(SCOUT_API_PROTOCOL),
    host: SCOUT_API_ENDPOINT,
    basePath: SCOUT_API_STAGE || defaultTarget.basePath,
    region: SCOUT_API_REGION || defaultTarget.region
  }
}

function buildApiUrl(target, path, queryString = '') {
  return `${target.protocol}://${target.host}${target.basePath}${path}${queryString}`
}

const slice = createSlice({
  name: 'api',
  initialState,
  reducers: {
    ['application/config'](state, action) {
      state.config = action.payload.data
    },
    ['user/get'](state, action) {
      state.user = action.payload.data
    },
    ['user/invite'](state, action) {
      apiDebug(action)
    },
    ['user/delete'](state, action) {
      apiDebug(action)
    },
    ['admin/setattribute'](state, action) {
      apiDebug(action)
    },
    ['site/create'](state, action) {
      apiDebug(action)
    },
    ['admin/overview'](state, action) {
      apiDebug("**ADMIN SITES**".site)
      apiDebug(action)
      state.sites = action.payload.data.data
    },
    ['site/get'](state, action) {
      apiDebug("**SITE**", action.payload.data)
      state.site = action.payload.data
    },
    ['site/get/data'](state, action) {
      apiDebug("**SITE DATA**", action.payload.data)
      //merge data into state.site
      //'users' on the site record is a simplified denormalized copy ({username, email, phone})
      //written by the backend for server-side use - never let it clobber the rich
      //Cognito user objects (Attributes[]) delivered by 'site/get'
      const data = { ...action.payload.data }
      delete data.users
      state.site = { ...state.site, ...data }
    },
    ['asset/list'](state, action) {
      apiDebug(action)
      let validAssets = []

      _.forEach(action.payload.data, (a) => {
        if (a.input || (a.assetType && a.assetType == "override")) {
          const currentAsset = state.assets.find(asset => asset.SK === a.SK || asset.id === a.id)
          a = mergeFreshAssetReadings(a, currentAsset)
          //a.lastUpdated = new Date().getTime()
          validAssets.push(a)
        }
      })
      validAssets.sort(function (a, b) { return a.order - b.order });

      state.assets = validAssets
    },
    ['asset/create/bulk'](state, action) {
      apiDebug(action)
    },
    ['asset/create'](state, action) {
      apiDebug(action)
    },
    ['asset/input/create'](state, action) {
      apiDebug(action)
    },
    ['asset/output/create'](state, action) {
      apiDebug(action)
    },
    ['asset/input/log/list'](state, action) {
      apiDebug(action)
    },
    ['asset/output/switch'](state, action) {
      apiDebug(action)
    },
    ['asset/input/target/set'](state, action) {
      apiDebug(action)
    },
    ['asset/update'](state, action) {
      apiDebug(action)
    },
    ['asset/config/set'](state, action) {
      apiDebug(action)
    },
    ['site/config/set'](state, action) {
      apiDebug(action)
      const data = action?.payload?.data || {}
      if (!state.site || data?.success === false || data?.error) return
      const payload = action?.payload?.request || {}
      state.site.config = { ...state.site.config, ...payload }
    },
    ['site/config/history'](state, action) {
      apiDebug(action)
    },
    ['site/lockout/set'](state, action) {
      apiDebug(action)
    },
    ['site/assistant/config'](state, action) {
      const data = action?.payload?.data || {}
      if (!state.site || data?.success === false || data?.error) return
      if (Object.prototype.hasOwnProperty.call(data, 'assistantConfig')) {
        state.site.assistantConfig = data.assistantConfig || {}
      }
      if (Object.prototype.hasOwnProperty.call(data, 'energyPlan')) {
        state.site.energyPlan = data.energyPlan || null
      }
    },
    ['site/notifications/set'](state, action) {
      apiDebug(action)
    },
    ['site/tags/set'](state, action) {
      apiDebug(action)
    },
    ['site/activate'](state, action) {
      apiDebug(action)
    },
    ['asset/secondary/set'](state, action) {
      apiDebug(action)
    },
    ['asset/output/log/list'](state, action) {
      apiDebug(action)
    },
    ['asset/priority/update'](state, action) {
      apiDebug(action)
    },
    ['asset/superpriority/update'](state, action) {
      apiDebug(action)
    },
    ['asset/hfmode/update'](state, action) {
      apiDebug(action)
    },
    ['asset/overchill/update'](state, action) {
      apiDebug(action)
    },
    ['asset/altswitch/update'](state, action) {
      apiDebug(action)
    },
    ['site/energy/log/list'](state, action) {
      apiDebug("site/energy/log/list", action.payload.data)
      state.energy = action.payload.data
    },
    ['site/power/log/list'](state, action) {
      apiDebug("site/power/log/list", action.payload.data)
      state.power = action.payload.data
    },
    ['site/logs/get'](state, action) {

      state.siteLogs = [...action.payload.data.data]
      apiDebug("SITELOGS", state.siteLogs)
    },
    ['site/logs/retrieve'](state, action) {
      apiDebug(action)
    },
    ['template/create'](state, action) {
      apiDebug(action)
    },
    ['template/update'](state, action) {
      apiDebug(action)
    },
    ['template/list'](state, action) {
      let rawTemplates = action.payload.data
      let templates = []
      _.forEach(rawTemplates, (t) => {
        if (t.name.toLowerCase() != "delete") templates.push(t)
      })
      state.templates = templates.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
    },
    ['site/yeast/list'](state, action) {
      state.yeasts = action?.payload?.data?.data || [];
      apiDebug("STYLES", state.yeasts)
    },
    ['brew/create'](state, action) {
      apiDebug(action)
    },
    ['brew/update'](state, action) {
      apiDebug(action)
    },
    ['brew/template/update'](state, action) {
      apiDebug(action)
    },
    ['brew/finish'](state, action) {
      apiDebug(action)
    },
    ['brew/list'](state, action) {
      apiDebug(action)
    },
    ['brew/log/list'](state, action) {
      apiDebug(action)
    },
    ['wastewater/list'](state, action) {
      apiDebug(action)
    },
    ['wastewater/control'](state, action) {
      apiDebug(action)
    },
    ['status/get'](state, action) {
      apiDebug(action)
    },
    ['status/logs/get'](state, action) {
      apiDebug(action)
    },
    ['status/output/toggle'](state, action) {
      apiDebug(action)
    },
    ['payment/checkout'](state, action) {
      apiDebug(action)
    },
    ['subscription'](state, action) {
      apiDebug(action)
    },
    ['test/outputs'](state, action) {
      apiDebug(action)
    },
    ['refresh'](state, action) {
      apiDebug(action)
    },
    ['site/contract/sign'](state, action) {
      apiDebug(action)
    },
    ['note/list'](state, action) {
      apiDebug(action)
    },
    ['note/create'](state, action) {
      apiDebug(action)
    },
    ['note/update'](state, action) {
      apiDebug(action)
    },
    ['brew/transfer'](state, action) {
      apiDebug(action)
    },
    ['brew/delete'](state, action) {
      apiDebug(action)
    },
    ['reports/batch'](state, action) {
      apiDebug(action)
    },
    ['api/key/list'](state, action) {
      state.apiKeys = action.payload.data
    },
    ['api/key/create'](state, action) {
      apiDebug(action)
    },
    ['api/key/delete'](state, action) {
      apiDebug(action)
    },
    ['integrate/associate'](state, action) {
      apiDebug(action)
    },
    ['integrate/plaato/device/list'](state, action) {
      let pDevices = []
      _.forEach(action.payload.data, (p) => {
        if (!p.archived.isArchived) {
          pDevices.push(p)
        }
      })
      state.plaatoDevices = pDevices
      apiDebug("PLAATO", action.payload.data)
    },
    ['integrate/plaato/device/assign'](state, action) {
      apiDebug(action)
    },
    ['integrate/amber/sites/list'](state, action) {
      apiDebug(action)
      let aSites = []
      _.forEach(action.payload.data, (p) => {
        aSites.push(p)
      })
      state.amberSites = aSites
      apiDebug("AMBER SITES", state.amberSites)
    },
    ['integrate/amber/config'](state, action) {
      apiDebug(action)
    },
    ['integrate/amber/prices'](state, action) {
      state.amberPrices = action.payload.data;
      apiDebug(action)
    },
    ['integrate/vintrace/config'](state, action) {
      apiDebug(action)
    },
    ['integrate/vintrace/link'](state, action) {
      apiDebug(action)
    },
    ['integrate/breww/config'](state, action) {
      apiDebug(action)
    },
    ['integrate/google/sheets/config'](state, action) {
      apiDebug(action)
    },
    ['integrate/google/sheets/get'](state, action) {
      apiDebug(action)
    },
    ['site/proactive/update'](state, action) {
      apiDebug(action)
    },
    ['asset/dynamicglycol/update'](state, action) {
      apiDebug(action)
    },
    ['asset/input/primary'](state, action) {
      apiDebug(action)
    },
    ['user/update'](state, action) {
      apiDebug(action)
    },
    ['frigid/scan'](state, action) {
      apiDebug(action)
    },
    ['brew/log/update'](state, action) {
      apiDebug(action)
    },
    ['flow/git/install'](state, action) {
      apiDebug(action)
    },
    ['flow/git/update'](state, action) {
      apiDebug(action)
    },
    ['milesight/token/refresh'](state, action) {
      apiDebug(action)
    },
    ['assistant/triageboard/get'](state, action) {
      const payloadData = action?.payload?.data
      if (!payloadData) {
        state.triageBoard = null
        return
      }
      state.triageBoard = payloadData.triageBoard || payloadData
    },
    ['assistant/asset/insight'](state, action) {
      apiDebug(action)
    },
    ['assistant/inferencechart/get'](state, action) {
      apiDebug(action)
    },
    ['assistant/scout/refresh'](state, action) {
      state.scoutRefresh = action.payload.data
    },
    ['assistant/scout/refresh/quota'](state, action) {
      state.scoutRefreshQuota = action.payload.data
    },
    ['assistant/scout/tank/refresh'](state, action) {
      state.scoutRefresh = action.payload.data
    },
    ['assistant/actions/execute'](state, action) {
      state.scoutActionExecution = action.payload.data
    },
    ['assistant/actions/history'](state, action) {
      state.scoutActionHistory = action.payload.data
    },
    ['assistant/scout/context'](state, action) {
      state.scoutContext = action.payload.data
    },
    ['assistant/chat'](state, action) {
      state.scoutChat = action.payload.data
    },
    ['assistant/efficiency/forecast'](state, action) {
      state.assistantEfficiencyForecast = action.payload.data
    },
    ['assistant/efficiency/forecast/get'](state, action) {
      state.assistantEfficiencyForecast = action.payload.data
    },
    ['assistant/efficiency/train'](state, action) {
      state.assistantEfficiencyForecast = action.payload.data
    },
    ['assistant/models/bootstrap'](state, action) {
      apiDebug(action)
    },
    ['assistant/models/training/set'](state, action) {
      apiDebug(action)
    },
    ['assistant/models/batch/clear'](state, action) {
      apiDebug(action)
    },
    ['assistant/models/retrain'](state, action) {
      apiDebug(action)
    },
    ['asset/pressure/override'](state, action) {
      apiDebug(action)
    },
    ['asset/override'](state, action) {
      apiDebug(action)
    },
    ['asset/density/calibrate'](state, action) {
      apiDebug(action)
    },
    ['site/version/acknowledge'](state, action) {
      const { version, email } = action.payload.data;
      if (state.site && email && version) {
        if (!state.site.acknowledgedVersions || typeof state.site.acknowledgedVersions !== 'object') {
          state.site.acknowledgedVersions = {};
        }
        if (!Array.isArray(state.site.acknowledgedVersions[version])) {
          state.site.acknowledgedVersions[version] = [];
        }
        if (!state.site.acknowledgedVersions[version].includes(email)) {
          state.site.acknowledgedVersions[version].push(email);
        }
      }
    },
    updateAssetsFromStream: (state, action) => {
      // Replace the old assets with the new, updated array
      apiDebug("Updating assets from stream", action.payload);
      state.assets = action.payload;
      //
    },
    ['site/energy/meters'](state, action) {
      apiDebug(action)
    },
    ['site/energy/meters/config'](state, action) {
      apiDebug(action)
    },
    ['site/meross/history/list'](state, action) {
      apiDebug(action)
    },
    // ERP reducers
    ['erp/product-type/list'](state, action) {
      state.erpProductTypes = (action.payload.data && action.payload.data.productTypes) || []
    },
    ['erp/product-type/get'](state, action) {
      state.erpProductType = (action.payload.data && action.payload.data.productType) || null
    },
    ['erp/process-flow/list'](state, action) {
      state.erpProcessFlows = (action.payload.data && action.payload.data.processFlows) || []
    },
    ['erp/process-flow/get'](state, action) {
      state.erpProcessFlow = (action.payload.data && action.payload.data.processFlow) || null
    },
    ['erp/ingredient/list'](state, action) {
      state.erpIngredients = (action.payload.data && action.payload.data.ingredients) || []
    },
    ['erp/ingredient/get'](state, action) {
      state.erpIngredient = (action.payload.data && action.payload.data.ingredient) || null
    },
    ['erp/supplier/list'](state, action) {
      state.erpSuppliers = (action.payload.data && action.payload.data.suppliers) || []
    },
    ['erp/supplier/get'](state, action) {
      state.erpSupplier = (action.payload.data && action.payload.data.supplier) || null
    },
    ['erp/recipe/list'](state, action) {
      state.erpRecipes = (action.payload.data && action.payload.data.recipes) || []
    },
    ['erp/recipe/get'](state, action) {
      state.erpRecipe = (action.payload.data && action.payload.data.recipe) || null
    },
    ['erp/inventory/list'](state, action) {
      state.erpInventoryItems = (action.payload.data && action.payload.data.items) || []
    },
    ['erp/inventory/transactions'](state, action) {
      state.erpInventoryTransactions = (action.payload.data && action.payload.data.transactions) || []
    },
    ['erp/inventory/lots'](state, action) {
      state.erpInventoryLots = (action.payload.data && action.payload.data.lots) || []
    },
    ['erp/batch/list'](state, action) {
      state.erpBatches = (action.payload.data && action.payload.data.batches) || []
    },
    ['erp/batch/get'](state, action) {
      state.erpBatch = (action.payload.data && action.payload.data.batch) || null
      state.erpBatchLogs = (action.payload.data && action.payload.data.logs) || []
      state.erpBatchLabs = (action.payload.data && action.payload.data.labs) || []
    },
    ['erp/batch/logs'](state, action) {
      state.erpBatchLogs = (action.payload.data && action.payload.data.logs) || []
    },
    ['erp/batch/labs'](state, action) {
      state.erpBatchLabs = (action.payload.data && action.payload.data.labs) || []
    },
    ['erp/packaging-item/list'](state, action) {
      state.erpPackagingItems = (action.payload.data && action.payload.data.packagingItems) || []
    },
    ['erp/packaging-item/get'](state, action) {
      state.erpPackagingItem = (action.payload.data && action.payload.data.packagingItem) || null
    },
    ['erp/packaging-run/list'](state, action) {
      state.erpPackagingRuns = (action.payload.data && action.payload.data.packagingRuns) || []
    },
    ['erp/packaging-run/get'](state, action) {
      state.erpPackagingRun = (action.payload.data && action.payload.data.packagingRun) || null
    },
    ['erp/other-item/list'](state, action) {
      state.erpOtherItems = (action.payload.data && action.payload.data.otherItems) || []
    },
    ['erp/other-item/get'](state, action) {
      state.erpOtherItem = (action.payload.data && action.payload.data.otherItem) || null
    },
    ['erp/sku/list'](state, action) {
      state.erpSkus = (action.payload.data && action.payload.data.skus) || []
    },
    ['erp/sku/get'](state, action) {
      state.erpSku = (action.payload.data && action.payload.data.sku) || null
    },
    ['erp/excise/config'](state, action) {
      state.erpExciseConfig = (action.payload.data && action.payload.data.config) || null
    },
    ['erp/excise/rates'](state, action) {
      state.erpExciseRates = (action.payload.data && action.payload.data.rates) || []
    },
    ['erp/excise/amr'](state, action) {
      state.erpAmr = (action.payload.data && action.payload.data.amr) || null
    },
    ['erp/excise/return/list'](state, action) {
      state.erpExciseReturns = (action.payload.data && action.payload.data.exciseReturns) || []
    },
    ['erp/excise/return/get'](state, action) {
      state.erpExciseReturn = (action.payload.data && action.payload.data.exciseReturn) || null
    },
    ['erp/excise/dispatch/list'](state, action) {
      state.erpDispatches = (action.payload.data && action.payload.data.dispatches) || []
    },
    ['erp/excise/audit'](state, action) {
      state.erpExciseRecords = (action.payload.data && action.payload.data.records) || []
    },
    ['erp/excise/recall'](state, action) {
      state.erpRecallTrace = (action.payload.data && action.payload.data) || null
    },
    ['erp/customer/list'](state, action) {
      state.erpCustomers = (action.payload.data && action.payload.data.customers) || []
    },
    ['erp/customer/get'](state, action) {
      state.erpCustomer = (action.payload.data && action.payload.data.customer) || null
    },
    ['erp/customer/create'](state, action) {
      apiDebug(action)
    },
    ['erp/customer/update'](state, action) {
      apiDebug(action)
    },
    ['erp/customer/delete'](state, action) {
      apiDebug(action)
    },
    ['erp/order/list'](state, action) {
      state.erpOrders = (action.payload.data && action.payload.data.orders) || []
    },
    ['erp/order/get'](state, action) {
      state.erpOrder = (action.payload.data && action.payload.data.order) || null
    },
    ['erp/order/create'](state, action) {
      apiDebug(action)
    },
    ['erp/order/update'](state, action) {
      apiDebug(action)
    },
    ['erp/order/delete'](state, action) {
      apiDebug(action)
    },
    ['erp/order/fulfill'](state, action) {
      apiDebug(action)
    },

    // Picking
    ['erp/picking/list'](state, action) {
      state.erpPickingRuns = (action.payload.data && action.payload.data.pickingRuns) || []
    },
    ['erp/picking/get'](state, action) {
      state.erpPickingRun = (action.payload.data && action.payload.data.pickingRun) || null
    },
    ['erp/picking/create'](state, action) {
      apiDebug(action)
    },
    ['erp/picking/update'](state, action) {
      apiDebug(action)
    },
    ['erp/picking/delete'](state, action) {
      apiDebug(action)
    },
    ['erp/picking/complete'](state, action) {
      apiDebug(action)
    },

    // Shipping
    ['erp/shipping/list'](state, action) {
      state.erpShipments = (action.payload.data && action.payload.data.shipments) || []
    },
    ['erp/shipping/get'](state, action) {
      state.erpShipment = (action.payload.data && action.payload.data.shipment) || null
    },
    ['erp/shipping/create'](state, action) {
      apiDebug(action)
    },
    ['erp/shipping/update'](state, action) {
      apiDebug(action)
    },
    ['erp/shipping/delete'](state, action) {
      apiDebug(action)
    },
    ['erp/shipping/change-status'](state, action) {
      apiDebug(action)
    },

    // Purchase orders
    ['erp/po/list'](state, action) {
      state.erpPurchaseOrders = (action.payload.data && action.payload.data.purchaseOrders) || []
    },
    ['erp/po/get'](state, action) {
      state.erpPurchaseOrder = (action.payload.data && action.payload.data.purchaseOrder) || null
    },
    ['erp/po/create'](state, action) {
      apiDebug(action)
    },
    ['erp/po/update'](state, action) {
      apiDebug(action)
    },
    ['erp/po/delete'](state, action) {
      apiDebug(action)
    },
    ['erp/po/receive'](state, action) {
      apiDebug(action)
    },

    // Scheduling resources
    ['erp/resource/list'](state, action) {
      state.erpResources = (action.payload.data && action.payload.data.resources) || []
    },
    ['erp/resource/get'](state, action) {
      state.erpResource = (action.payload.data && action.payload.data.resource) || null
    },
    ['erp/resource/save'](state, action) {
      apiDebug(action)
    },
    ['erp/resource/delete'](state, action) {
      apiDebug(action)
    },
    ['erp/resource/event/list'](state, action) {
      state.erpResourceEvents = (action.payload.data && action.payload.data.events) || []
    },
    ['erp/resource/event/get'](state, action) {
      state.erpResourceEvent = (action.payload.data && action.payload.data.event) || null
    },
    ['erp/resource/event/save'](state, action) {
      apiDebug(action)
    },
    ['erp/resource/event/delete'](state, action) {
      apiDebug(action)
    },

    // Trash / Recycle Bin
    ['erp/trash/list'](state, action) {
      state.erpTrashEvents = (action.payload.data && action.payload.data.events) || []
    },
    ['erp/trash/restore'](state, action) {
      apiDebug(action)
    },
    ['erp/trash/purge'](state, action) {
      apiDebug(action)
    },

    // Barrels
    ['erp/barrel/list'](state, action) {
      state.erpBarrels = (action.payload.data && action.payload.data.barrels) || []
    },
    ['erp/barrel/get'](state, action) {
      state.erpBarrel = (action.payload.data && action.payload.data.barrel) || null
      state.erpBarrelEvents = (action.payload.data && action.payload.data.events) || []
    },
    ['erp/barrel/events'](state, action) {
      state.erpBarrelEvents = (action.payload.data && action.payload.data.events) || []
    },
    ['erp/barrel/create'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/update'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/delete'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/fill'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/top'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/transfer'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/empty'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/sample'](state, action) {
      apiDebug(action)
    },
    ['erp/barrel/event'](state, action) {
      apiDebug(action)
    },

    // Xero integration
    ['erp/integration/xero/status'](state, action) {
      state.erpXeroStatus = (action.payload.data && action.payload.data) || null
    },
    ['erp/integration/xero/auth-url'](state, action) {
      state.erpXeroAuthUrl = (action.payload.data && action.payload.data.url) || null
    },
    ['erp/integration/xero/disconnect'](state, action) {
      apiDebug(action)
      state.erpXeroStatus = { connected: false }
    },

    // Square POS integration
    ['erp/integration/square/status'](state, action) {
      state.erpSquareStatus = (action.payload.data && action.payload.data) || null
    },
    ['erp/integration/square/auth-url'](state, action) {
      state.erpSquareAuthUrl = (action.payload.data && action.payload.data.url) || null
    },
    ['erp/integration/square/disconnect'](state, action) {
      apiDebug(action)
      state.erpSquareStatus = { connected: false }
    },

    // GoTab POS integration
    ['erp/integration/gotab/status'](state, action) {
      state.erpGotabStatus = (action.payload.data && action.payload.data) || null
    },
    ['erp/integration/gotab/locations'](state, action) {
      apiDebug(action)
    },
    ['erp/integration/gotab/connect'](state, action) {
      apiDebug(action)
    },
    ['erp/integration/gotab/disconnect'](state, action) {
      apiDebug(action)
      state.erpGotabStatus = { connected: false }
    },
    ['erp/integration/gotab/reconcile'](state, action) {
      apiDebug(action)
    },

    ['erp/integration/xero/config'](state, action) {
      state.erpXeroConfig = (action.payload.data && action.payload.data) || null
    },
    ['erp/integration/square/config'](state, action) {
      state.erpSquareConfig = (action.payload.data && action.payload.data) || null
    },
    ['erp/integration/gotab/config'](state, action) {
      state.erpGotabConfig = (action.payload.data && action.payload.data) || null
    },

    // Kounta POS integration
    ['erp/integration/kounta/status'](state, action) {
      state.erpKountaStatus = (action.payload.data && action.payload.data) || null
    },
    ['erp/integration/kounta/auth-url'](state, action) {
      state.erpKountaAuthUrl = (action.payload.data && action.payload.data.url) || null
    },
    ['erp/integration/kounta/disconnect'](state, action) {
      apiDebug(action)
      state.erpKountaStatus = { connected: false }
    },
    ['erp/integration/kounta/sites'](state, action) {
      apiDebug(action)
    },
    ['erp/integration/kounta/register-webhook'](state, action) {
      apiDebug(action)
    },
    ['erp/integration/kounta/reconcile'](state, action) {
      apiDebug(action)
    },
    ['erp/integration/kounta/config'](state, action) {
      state.erpKountaConfig = (action.payload.data && action.payload.data) || null
    }
  },
})

// Reducer
export default slice.reducer

export const { updateAssetsFromStream } = slice.actions;

export function getData(path, queryString = {}, signRequest = false) {

  return function (dispatch) {
    let action = path
    path = '/' + path
    queryString = buildQueryString(queryString)

    return new Promise(async (resolve, reject) => {
      let response = await _getData(path, queryString, signRequest)
      if (response) {
        dispatch(slice.actions[action]({ data: response }))
        resolve(response)
      } else {
        try {
          dispatch(slice.actions[action]({ data: null }))
        } catch (e) {
          apiDebug("Error dispatching", e)
        }
        resolve(false)
      }
    })
  }
}

export function fetchData(path, queryString = {}, signRequest = false) {
  return _getData('/' + path, buildQueryString(queryString), signRequest)
}

async function parseApiResponse(res) {
  const text = await res.text()

  if (!text) {
    return false
  }

  try {
    return JSON.parse(text)
  } catch (e) {
    return text
  }
}

async function _getData(path, queryString = '', signRequest = false) {
  const target = resolveApiTarget(path)

  let promise = signRequest
    ? new Promise(async (resolve, reject) => {
      var options = {
        method: 'GET',
        service: 'execute-api',
        region: target.region,
        host: target.host,
        path: target.basePath + path + queryString,
        url: buildApiUrl(target, path, queryString),
      }
      const credentials = await Auth.currentCredentials()
      const { accessKeyId, secretAccessKey, sessionToken } = credentials
      const request = aws4.sign(options, {
        accessKeyId,
        secretAccessKey,
        sessionToken,
      })
      delete request.headers.Host
      let res
      //apiDebug(options)
      try {
        res = await fetch(options.url, {
          headers: request.headers,
        })
      } catch (e) {
        apiDebug('Error fetching, ', e)
      }

      if (!res || !res.ok) {
        resolve(false)
      } else {
        apiDebug('res', JSON.stringify(res));
        const data = await parseApiResponse(res)
        if (data && !data.error) {
          // apiDebug(data)
          resolve(data)
        } else {
          apiDebug("There was an error", data)
          //if (data.error) alert(data.message || 'There was an error.')
          resolve(false)
        }
      }
    })
    : new Promise(async (resolve, reject) => {
      const res = await fetch(buildApiUrl(target, path, queryString))
      if (!res || !res.ok) {
        resolve(false)
      } else {
        apiDebug('res', res)
        try {
          const data = await parseApiResponse(res)
          if (data) {
            resolve(data)
          } else {
            resolve(false)
          }
        } catch (e) {
          apiDebug('Error', e)
          resolve(false)
        }
      }
    })

  return promise
}

export function postData(path, data, signRequest = false) {
  return function (dispatch) {
    let action = path
    path = '/' + path
    return new Promise(async (resolve, reject) => {
      let response = await _postData(path, data, signRequest)
      if (response) {
        const reducerAction = slice.actions[action]
        if (reducerAction) {
          dispatch(reducerAction({ data: response, request: data }))
        }
        resolve(response)
      } else {
        resolve(false)
      }
    })
  }
}

async function _postData(path, data, signRequest = false) {
  const target = resolveApiTarget(path)
  let promise = signRequest
    ? new Promise(async (resolve, reject) => {
      try {
        var options = {
          method: 'POST',
          service: 'execute-api',
          region: target.region,
          host: target.host,
          path: target.basePath + path,
          url: buildApiUrl(target, path),
          body: JSON.stringify(data),
        }
        const credentials = await Auth.currentCredentials()
        const { accessKeyId, secretAccessKey, sessionToken } = credentials
        const request = aws4.sign(options, {
          accessKeyId,
          secretAccessKey,
          sessionToken,
        })
        delete request.headers.Host
        const res = await fetch(options.url, {
          method: 'POST',
          headers: request.headers,
          body: JSON.stringify(data),
        })
        if (!res || !res.ok) {
          let errorData = null
          try {
            const text = await res.text()
            errorData = JSON.parse(text)
          } catch (e) {
            // ignore non-JSON error bodies
          }
          resolve(errorData || false)
        } else {
          let data = await res.text()
          data = JSON.parse(data)
          apiDebug('data', data)
          if (data && !data.error) {
            resolve(data)
          } else {
            if (data.error) alert(data.message || 'There was an error.')
            resolve(false)
          }
        }
      } catch (e) {
        apiDebug('error with signed postData', e)
        resolve(false)
      }
    })
    : new Promise(async (resolve, reject) => {
      try {
        const res = await fetch(buildApiUrl(target, path), {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
        if (!res || !res.ok) {
          apiDebug('error with unsigned postData', res)
          let errorData = null
          try {
            errorData = await res.json()
          } catch (e) {
            // ignore non-JSON error bodies
          }
          resolve(errorData || false)
        } else {
          apiDebug('res', res)

          const data = await res.json()
          if (data) {
            resolve(data)
          } else {
            resolve(false)
          }
        }
      } catch (e) {
        apiDebug('error with unsigned postData', e)
        resolve(false)
      }
    })

  return promise
}


export function localRequest(path, method, req) {


  return function (dispatch) {
    apiDebug(req)
    path = '/' + path
    let action = "frigid" + path
    const url = "https://" + req.hostname + path
    apiDebug("teltonika:", req.teltonika)
    return new Promise(async (resolve, reject) => {
      let request = {
        method: method,
        headers: { 'Content-Type': 'application/json' },
      }
      if (method == "post") request.body = JSON.stringify(req)

      const res = await fetch(
        url,
        request,
      )
      if (!res || !res.ok) {
        apiDebug('error with postLocalData', res)
        resolve(false)
      } else {
        apiDebug('res', res)

        const data = await res.json()

        if (data) {
          try {
            dispatch(slice.actions[action]({ data }))
          } catch (e) {
            apiDebug(e)
          }
          resolve(data)
        } else {
          resolve(false)
        }
      }

    })
  }
}

function buildQueryString(obj) {
  if (!obj) return ''
  let qs = '?'
  for (var k in obj) {
    if (qs != '?') qs += '&'
    qs += encodeURIComponent(k) + '=' + encodeURIComponent(obj[k])
  }
  return qs
}
